import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogIn } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signedOutReason = searchParams.get('reason');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      if (result.data.role === 'admin') {
        navigate('/admin');
      } else if (result.data.role === 'teacher') {
        navigate('/teacher');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6">
      <div className="w-full max-w-md">
        <div className="clay-card p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-4">
              <LogIn className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-outfit font-extrabold tracking-tight text-slate-900 mb-2">
              Welcome Back
            </h1>
            <p className="text-base font-figtree text-slate-600">
              Sign in to continue your learning journey
            </p>
          </div>

          {signedOutReason === 'password_changed' && !error && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="password-changed-notice">
              <p className="text-sm text-green-800 font-figtree">Your password was changed. Please sign in with the new password.</p>
            </div>
          )}

          {signedOutReason && signedOutReason !== 'password_changed' && !error && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl" data-testid="signed-out-notice">
              <p className="text-sm text-orange-800 font-figtree">
                {signedOutReason === 'other_device'
                  ? 'You were signed out because your account was signed in on another device. Accounts can be used on one device at a time.'
                  : 'Your session has ended. Please sign in again.'}
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="login-error">
              <p className="text-sm text-red-600 font-figtree">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-outfit font-bold text-slate-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-outfit font-bold text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
              />
            </div>

            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-figtree font-bold" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full clay-button-primary py-6 text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm font-figtree text-slate-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-bold" data-testid="register-link">
                Create Account
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link
              to="/"
              className="text-sm text-slate-500 hover:text-slate-700 font-figtree"
              data-testid="back-home-link"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;